import React, { useState } from 'react';
import { X, Smartphone, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';

const ForgotPinModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('request'); // 'request', 'verify', 'success'
  const [telephone, setTelephone] = useState('');
  const [code, setCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRequestCode = async () => {
    if (!telephone || telephone.length !== 9) {
      setError('Entrez un numéro valide (9 chiffres)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Demander un code de réinitialisation
      await api.post('/api/auth/employee/reset-pin-request', {
        telephone: telephone
      });

      // Note: Le backend ne révèle pas si le numéro existe (sécurité)
      // Il retourne toujours un succès
      setStep('verify');
    } catch (err) {
      console.error('Erreur demande code:', err);
      setError(
        err.response?.data?.detail || 
        'Erreur lors de la demande. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPin = async () => {
    if (!code || code.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }

    if (!newPin || newPin.length !== 4) {
      setError('Le nouveau PIN doit contenir 4 chiffres');
      return;
    }

    if (newPin !== confirmPin) {
      setError('Les PINs ne correspondent pas');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Réinitialiser le PIN
      await api.post('/api/auth/employee/reset-pin-confirm', {
        telephone: telephone,
        code: code,
        new_pin: newPin
      });

      setStep('success');
      
      // Fermer après 2 secondes
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Erreur réinitialisation PIN:', err);
      setError(
        err.response?.data?.detail || 
        'Code invalide ou expiré. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('request');
    setTelephone('');
    setCode('');
    setNewPin('');
    setConfirmPin('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                PIN oublié ?
              </h3>
              <p className="text-sm text-gray-500">Réinitialisez votre code PIN</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {step === 'request' && (
            // Étape 1 : Demander le code
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Comment ça marche ?</strong>
                </p>
                <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Entrez votre numéro de téléphone</li>
                  <li>Si enregistré, vous recevrez un code par SMS</li>
                  <li>Entrez le code et choisissez votre nouveau PIN</li>
                </ol>
                <p className="text-xs text-blue-600 mt-2">
                  💡 Numéro non enregistré ? Contactez votre service RH.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de téléphone
                </label>
                <input
                  type="text"
                  value={telephone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                    setTelephone(value);
                  }}
                  placeholder="622123456"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength="9"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">Format: 9 chiffres commençant par 6</p>
              </div>

              <button
                onClick={handleRequestCode}
                disabled={loading || telephone.length !== 9}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Smartphone className="w-5 h-5" />
                    Recevoir le code SMS
                  </>
                )}
              </button>
            </div>
          )}

          {step === 'verify' && (
            // Étape 2 : Vérifier le code et choisir nouveau PIN
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-medium mb-1">
                    Demande envoyée
                  </p>
                  <p className="text-sm text-blue-700">
                    Si ce numéro ({telephone}) est enregistré, vous recevrez un code SMS dans quelques instants.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    💡 Pas de SMS reçu ? Le numéro n'est peut-être pas enregistré. Contactez votre RH.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code de vérification (6 chiffres)
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(value);
                  }}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength="6"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau PIN (4 chiffres)
                </label>
                <input
                  type="password"
                  value={newPin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setNewPin(value);
                  }}
                  placeholder="••••"
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength="4"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le PIN
                </label>
                <input
                  type="password"
                  value={confirmPin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setConfirmPin(value);
                  }}
                  placeholder="••••"
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength="4"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setStep('request');
                    setCode('');
                    setNewPin('');
                    setConfirmPin('');
                    setError(null);
                  }}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Retour
                </button>
                <button
                  onClick={handleResetPin}
                  disabled={loading || code.length !== 6 || newPin.length !== 4 || confirmPin.length !== 4}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Traitement...' : 'Réinitialiser'}
                </button>
              </div>
            </div>
          )}

          {step === 'success' && (
            // Étape 3 : Succès
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">
                PIN réinitialisé !
              </h4>
              <p className="text-gray-600">
                Vous pouvez maintenant vous connecter avec votre nouveau PIN
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPinModal;
